import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../../utils/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTutorial } from '../../contexts/TutorialContext';
import { normalizeProgressBarStatus } from '../../utils/executionProgress';
import CreateBugModal from './CreateBugModal';

// Ordem/cor de cada status na barra única de progresso — só o que conta como
// progresso entra na barra (bug/melhoria/bloqueado/retestar); pendente e em
// andamento aparecem só na legenda abaixo, sem pintar espaço na barra, senão
// ela fica com uma cor cobrindo tudo e parece 100% mesmo sem estar.
const CYCLE_PROGRESS_SEGMENTS = [
  { key: 'completed', color: '#1C9A6C', label: 'Aprovado', icon: 'check' },
  { key: 'bug', color: '#D6394A', label: 'Bug', icon: 'bug' },
  { key: 'improvement', color: '#B4740A', label: 'Melhoria', icon: 'trend-up' },
  { key: 'blocked', color: '#5B6472', label: 'Bloqueado', icon: 'blocked' },
  { key: 'retest', color: '#6D4FCC', label: 'Retestar', icon: 'retry' }
];

// Explicação de quando usar cada status, no tooltip da tag.
const STATUS_HELP = {
  planned: 'Estado inicial: o caso está vinculado ao ciclo, mas ainda não foi executado nenhuma vez.',
  pending: 'Está na fila de execução — o QA ainda não começou a testar este caso.',
  running: 'Execução em andamento: o QA já iniciou este caso e ainda não registrou o resultado.',
  completed: 'O caso foi executado e o resultado obtido bateu com o esperado. Use quando o cenário passou sem problemas.',
  bug: 'O caso foi executado e encontrou uma falha real na funcionalidade testada. Ao marcar, um bug é criado automaticamente e vinculado a este caso.',
  improvement: 'O caso funcionou como esperado, mas você identificou algo que poderia melhorar (UX, clareza, performance) — não é uma falha da funcionalidade.',
  blocked: 'Use quando não for possível executar o caso por um motivo externo ao teste — ambiente indisponível, dependência de outra etapa/caso, ou dado de teste ausente. Diferente de Bug: aqui a funcionalidade não chegou a ser testada.',
  retest: 'Uma correção foi aplicada em um bug ou melhoria anterior deste caso, e ele precisa ser executado de novo para confirmar que o problema foi resolvido.'
};

// Ícones SVG (traço uniforme, 16px) — um por status, com o desenho que
// convencionalmente representa aquele conceito (o mesmo tipo de ícone usado
// em rastreadores de bug/QA: inseto p/ bug, seta p/ cima p/ melhoria, sinal
// de bloqueio, seta circular p/ retestar, play p/ em andamento).
const StatusIcon = ({ type, color = 'currentColor', size = 14 }) => {
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (type) {
    case 'check':
      return <svg {...common}><polyline points="5 13 9.5 17.5 19 7" /></svg>;
    case 'bug':
      return (
        <svg {...common}>
          <rect x="8" y="9" width="8" height="9" rx="4" />
          <path d="M9 9c0-1.7 1.3-3 3-3s3 1.3 3 3M5 12H8M16 12h3M6 16l2.2-1M17.8 15 16 16M6 19l2.6-1.7M17.4 17.3 18 19M12 6V4" />
        </svg>
      );
    case 'trend-up':
      return <svg {...common}><polyline points="4 16 10 10 14 14 20 6" /><polyline points="14 6 20 6 20 12" /></svg>;
    case 'blocked':
      return <svg {...common}><circle cx="12" cy="12" r="8.5" /><line x1="6.5" y1="17.5" x2="17.5" y2="6.5" /></svg>;
    case 'retry':
      return (
        <svg {...common}>
          <path d="M4.5 12a7.5 7.5 0 0 1 12.6-5.5" />
          <path d="M19.5 12a7.5 7.5 0 0 1-12.6 5.5" />
          <polyline points="17 4.5 17.4 7.8 14.1 8" />
          <polyline points="7 19.5 6.6 16.2 9.9 16" />
        </svg>
      );
    case 'play':
      return <svg {...common} fill={color} stroke="none"><path d="M8.5 6.5v11l9-5.5z" /></svg>;
    case 'circle':
      return <svg {...common}><circle cx="12" cy="12" r="7" /></svg>;
    default:
      return null;
  }
};

/**
 * Modal para listar os casos vinculados a um Ciclo de Teste
 * e permitir a alteração direta do status de cada caso.
 */
const CycleCasesModal = ({ isOpen, onClose, cycleId, runId, executionId = null, isReadOnly = false }) => {
  const { showError, showSuccess } = useNotification();
  const { hasPermission, user } = useAuth();
  const tutorial = useTutorial();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [downloadingNotebook, setDownloadingNotebook] = useState(false);
  const inlineRootRef = useRef(null);
  const [inlineHeight, setInlineHeight] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    const el = inlineRootRef.current;
    if (!el) return;
    const ZOOM_FACTOR = 0.78; // deve casar com o zoom aplicado em .app-content (AppLayout.css)
    const recalc = () => {
      const top = el.getBoundingClientRect().top;
      const available = (window.innerHeight - top - 12) / ZOOM_FACTOR;
      setInlineHeight(Math.max(available, 400));
    };
    recalc();
    window.addEventListener('resize', recalc);
    return () => window.removeEventListener('resize', recalc);
  }, [isOpen]);
  const canExportReports = hasPermission?.('reports.export', ['ADMIN', 'ADM', 'QA', 'GERENCIAL']);
  const [cases, setCases] = useState([]);
  const [statusByCaseId, setStatusByCaseId] = useState({});
  const [updatingCaseId, setUpdatingCaseId] = useState(null);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [statusMenuPosition, setStatusMenuPosition] = useState(null);
  const statusMenuTriggerRef = useRef(null);
  const [projectId, setProjectId] = useState(null);
  const [statusDefinitions, setStatusDefinitions] = useState([]);
  const [statusOverrides, setStatusOverrides] = useState([]);
  const [correctionByCaseId, setCorrectionByCaseId] = useState({});
  const [cycleStatus, setCycleStatus] = useState(null);
  const [cycleJiraStoryKey, setCycleJiraStoryKey] = useState(null); // Armazena a story key do ciclo
  // Mapa de issues por caseId (última atualização), para bloquear alterações quando necessário
  const [issuesByCaseId, setIssuesByCaseId] = useState({});
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [folderFilter, setFolderFilter] = useState('all');
  const selectedCase = useMemo(() => cases.find(c => String(c.id) === String(selectedCaseId)) || null, [cases, selectedCaseId]);
  const folderFilterOptions = useMemo(() => {
    const options = [];
    const seen = new Set();

    (Array.isArray(cases) ? cases : []).forEach((testCase) => {
      const value = String(testCase.folder_id || 'main');
      if (seen.has(value)) return;
      seen.add(value);
      options.push({
        value,
        label: testCase.folder_path || testCase.folder_name || 'MAIN',
        count: 1
      });
    });

    options.forEach((option) => {
      option.count = (cases || []).filter(testCase => String(testCase.folder_id || 'main') === option.value).length;
    });

    return options;
  }, [cases]);
  const visibleCases = useMemo(() => {
    if (folderFilter === 'all') return cases;
    return (cases || []).filter(testCase => String(testCase.folder_id || 'main') === folderFilter);
  }, [cases, folderFilter]);
  const [details, setDetails] = useState({ description: '', preconditions: '', steps: '' });
  const [savingDetails, setSavingDetails] = useState(false);
  const [timerDisplay, setTimerDisplay] = useState(null);
  const [activeCaseStartedAt, setActiveCaseStartedAt] = useState(null);
  const timerIntervalRef = useRef(null);
  const [editingDuration, setEditingDuration] = useState(false);
  const [durationInputs, setDurationInputs] = useState({ h: '0', m: '0', s: '0' });
  const [savingDuration, setSavingDuration] = useState(false);

  const formatElapsed = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  };

  // Estado para o modal de criação de bug
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [pendingIssueCaseId, setPendingIssueCaseId] = useState(null);
  const [pendingIssueType, setPendingIssueType] = useState('bug');
  
  // Estados para evidências
  const [evidences, setEvidences] = useState([]);
  const [loadingEvidences, setLoadingEvidences] = useState(false);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const fileInputRef = React.useRef(null);
  
  // Estado para preview de evidência
  const [previewEvidence, setPreviewEvidence] = useState(null);
  
  // Estado para confirmação de exclusão de evidência
  const [evidenceToDelete, setEvidenceToDelete] = useState(null);

  const subtasksTriggeredRef = React.useRef(false);

  // Função para criar subtasks automaticamente
  const createExecutionSubtasks = useCallback(async (cycleId, storyKey) => {
    if (!cycleId || !storyKey) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl('integrations/jira-basic/create-execution-subtasks'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ cycleId, storyKey })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (data.created > 0) {
            showSuccess('Subtasks criadas e vinculadas ao Jira!');
        }
      }
    } catch (e) {
      console.error('Erro ao criar subtasks:', e);
    }
  }, [showSuccess]);

  // Função para atualizar status da subtask
  const updateSubtaskStatus = async (caseId, cycleId, status) => {
    if (!caseId || !cycleId || !status) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(getApiUrl('integrations/jira-basic/update-subtask-status'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ caseId, cycleId, status })
      });
    } catch (e) {
      console.error('Erro ao atualizar status da subtask:', e);
    }
  };

  const handleSaveDetails = async () => {
    if (savingDetails || effectiveReadOnly) return;
    if (!selectedCase) {
      showError('Selecione um caso na lista');
      return;
    }

    if (tutorial?.isTutorialMode) {
      setCases(prev => prev.map(tc => (
        String(tc.id) === String(selectedCase.id)
          ? { ...tc, description: details.description, preconditions: details.preconditions, steps: details.steps }
          : tc
      )));
      showSuccess('Detalhes do caso atualizados');
      return;
    }

    setSavingDetails(true);
    try {
      const res = await fetch(getApiUrl(`testcases/${selectedCase.id}`), {
        method: 'PUT',
        headers: tokenHeader(),
        body: JSON.stringify({
          title: selectedCase.title,
          description: details.description,
          preconditions: details.preconditions,
          steps: details.steps,
          expected_result: selectedCase.expected_result || '',
          priority: selectedCase.priority || 'medium',
          status: selectedCase.status || 'draft',
          project_id: selectedCase.project_id
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(data?.error || 'Erro ao salvar detalhes');
      } else {
        const updated = data?.data || data?.testCase || null;
        setCases(prev => prev.map(tc => String(tc.id) === String(selectedCase.id) ? { ...tc, ...(updated || {}), description: details.description, preconditions: details.preconditions, steps: details.steps } : tc));
        showSuccess('Detalhes do caso atualizados');
      }
    } catch (_) {
      showError('Falha ao salvar detalhes');
    } finally {
      setSavingDetails(false);
    }
  };

  const tokenHeader = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
  });

  // Carregar definições globais de status (domínio test_cycle)
  const fetchStatusDefinitions = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl('status?domain=test_cycle&active=true'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      setStatusDefinitions(data?.data || data || []);
    } catch (_) {
      setStatusDefinitions([]);
    }
  };

  // Carregar overrides de status por projeto (ordenar/ocultar)
  const fetchProjectStatusOverrides = async (pid) => {
    if (!pid) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl(`status-overrides/${pid}?domain=test_cycle`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      setStatusOverrides(data?.data || data || []);
    } catch (_) {
      setStatusOverrides([]);
    }
  };

  // Carregar issues do projeto para identificar bloqueios por bug vinculado
  const fetchIssuesForProject = async (pid) => {
    if (!pid) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl(`issues?project_id=${pid}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const payload = await res.json().catch(() => ({}));
      const items = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);
      // Consolidar último item por caso de teste
      const map = {};
      items.forEach(it => {
        const cid = String(it.test_case_id || '');
        if (!cid) return;
        // Considerar apenas estados relevantes para bloqueio/aviso
        const st = String(it.status || '').toLowerCase();
        if (!['open','in_progress','reopened','fixed','rejected'].includes(st)) return;
        const prev = map[cid];
        const prevDate = prev ? new Date(prev.updated_at || prev.created_at || 0).getTime() : -1;
        const curDate = new Date(it.updated_at || it.created_at || 0).getTime();
        if (!prev || curDate >= prevDate) {
          map[cid] = it;
        }
      });
      setIssuesByCaseId(map);
    } catch (e) {
      // Silencioso: não bloquear fluxo do usuário por falha ao obter issues
      setIssuesByCaseId({});
    }
  };

  const fetchEvidences = useCallback(async (caseId) => {
    if (!caseId || !runId) return;
    setLoadingEvidences(true);
    try {
      if (tutorial?.isTutorialMode) {
        const list = tutorial?.getSimulatedEvidences?.(runId, caseId) || [];
        setEvidences(Array.isArray(list) ? list : []);
        return;
      }
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl(`evidences/test-case/${caseId}?execution_id=${runId}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setEvidences(data.data);
      } else {
        setEvidences([]);
      }
    } catch (error) {
      console.error('Erro ao buscar evidências:', error);
      showError('Erro ao carregar evidências');
    } finally {
      setLoadingEvidences(false);
    }
  }, [runId, showError, tutorial]);

  const handleUploadEvidence = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!selectedCaseId || !runId) {
      showError('Nenhum caso selecionado');
      return;
    }

    if (!canManageEvidences) {
      showError('Você não tem permissão para adicionar evidências.');
      return;
    }

    // Validar tamanho (15MB)
    if (file.size > 15 * 1024 * 1024) {
      showError('O arquivo deve ter no máximo 15MB');
      return;
    }

    setUploadingEvidence(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('test_case_id', selectedCaseId);
    formData.append('execution_id', runId);

    try {
      if (tutorial?.isTutorialMode) {
        const nowIso = new Date().toISOString();
        const created = {
          id: `tut-ev-${Date.now()}`,
          file_name: file.name,
          mime_type: file.type || 'application/octet-stream',
          created_at: nowIso,
          url: (() => {
            try {
              return URL.createObjectURL(file);
            } catch (_) {
              return null;
            }
          })(),
        };
        tutorial?.addSimulatedEvidence?.(runId, selectedCaseId, created);
        setEvidences(prev => [created, ...(Array.isArray(prev) ? prev : [])]);
        showSuccess('Evidência adicionada com sucesso');
        return;
      }
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl('evidences/upload'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      const data = await res.json();

      if (res.ok && data.success) {
        showSuccess('Evidência enviada com sucesso');
        fetchEvidences(selectedCaseId);
      } else {
        showError(data.error || 'Erro ao enviar evidência');
      }
    } catch (error) {
      console.error('Erro no upload:', error);
      showError('Falha ao enviar arquivo');
    } finally {
      setUploadingEvidence(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleViewEvidence = async (evidence) => {
    // Se já tiver URL pré-carregada (para imagens), usa ela
    if (evidence.url) {
        setPreviewEvidence(evidence);
        return;
    }

    // Se não, busca do backend
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl(`evidences/${evidence.id}/view`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success && data.url) {
        setPreviewEvidence({ ...evidence, url: data.url });
      } else {
        showError('Não foi possível obter a URL da evidência');
      }
    } catch (error) {
      showError('Erro ao abrir evidência');
    }
  };

  const handleDeleteEvidence = (evidence) => {
    if (!canManageEvidences) {
      showError('Você não tem permissão para excluir evidências.');
      return;
    }
    setEvidenceToDelete(evidence);
  };

  const confirmDeleteEvidence = async () => {
    if (!evidenceToDelete) return;
    if (!canManageEvidences) {
      showError('Você não tem permissão para excluir evidências.');
      setEvidenceToDelete(null);
      return;
    }

    try {
      if (tutorial?.isTutorialMode) {
        tutorial?.removeSimulatedEvidence?.(runId, selectedCaseId, evidenceToDelete.id);
        setEvidences(prev => (Array.isArray(prev) ? prev : []).filter(ev => String(ev.id) !== String(evidenceToDelete.id)));
        showSuccess('Evidência excluída');
        return;
      }
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl(`evidences/${evidenceToDelete.id}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        showSuccess('Evidência excluída');
        fetchEvidences(selectedCaseId);
      } else {
        showError('Erro ao excluir evidência');
      }
    } catch (error) {
      showError('Falha ao excluir evidência');
    } finally {
      setEvidenceToDelete(null);
    }
  };


  const isExecuting = useMemo(() => {
    const s = String(cycleStatus || '').toLowerCase();
    // Considera variações de "em andamento" — inclui 'planned' de propósito:
    // o fluxo "▶️ Executar" (Repositório de Testes > Ciclo de Testes) abre
    // esta modal direto num ciclo ainda planejado, sem passar por um passo
    // separado de "Iniciar" (esse existe só no Kanban de Execuções). O
    // backend já sabe iniciar o ciclo sozinho no primeiro status alterado
    // (rota PUT .../cases/:caseId/status) — sem isso aqui, o dropdown de
    // status ficava travado pra sempre nessa entrada, pra qualquer papel.
    const executingValues = new Set([
      'planned',
      'running',
      'active',
      'in_progress',
      'executando',
      'em_andamento',
      'em-andamento',
      'resumed'
    ]);
    return executingValues.has(s);
  }, [cycleStatus]);

  const statusOptions = useMemo(() => {
    const fallback = [
      { value: 'planned', label: 'Planejado' },
      { value: 'pending', label: 'Pendente' },
      { value: 'running', label: 'Executando' },
      { value: 'completed', label: 'Concluído' },
      { value: 'bug', label: 'Bug' },
      { value: 'improvement', label: 'Melhoria' },
      { value: 'blocked', label: 'Bloqueado' },
      { value: 'retest', label: 'Ag. Reteste' }
    ];
    const validKeys = ['planned', 'pending', 'running', 'completed', 'bug', 'improvement', 'blocked', 'retest'];
    const defs = Array.isArray(statusDefinitions)
      ? statusDefinitions.filter(d => d.domain === 'test_cycle' && d.is_active && d.scope_executions === true && validKeys.includes(d.key))
      : [];
    if (defs.length === 0) return isExecuting ? fallback.filter(o => o.value !== 'planned') : fallback;

    const overrideMap = {};
    if (Array.isArray(statusOverrides)) {
      statusOverrides.forEach(o => {
        if (o.domain === 'test_cycle' && validKeys.includes(o.key)) {
          overrideMap[o.key] = { hidden: !!o.hidden, order: Number(o.order) || 0 };
        }
      });
    }

    const filtered = defs.filter(d => !(overrideMap[d.key]?.hidden));
    filtered.sort((a, b) => {
      const oa = overrideMap[a.key]?.order ?? 9999;
      const ob = overrideMap[b.key]?.order ?? 9999;
      if (oa !== ob) return oa - ob;
      return (a.label || a.key).localeCompare(b.label || b.key);
    });
    const options = filtered
      .map(d => ({ value: d.key, label: d.label || d.key }))
      .filter(o => !isExecuting || o.value !== 'planned');
    if (!options.some(o => o.value === 'blocked')) {
      options.push({ value: 'blocked', label: 'Bloqueado' });
    }
    return options;
  }, [statusDefinitions, statusOverrides, isExecuting]);

  const getStatusBadgeClass = (status) => {
    const map = {
      planned: 'status-pending',
      running: 'status-warning',
      completed: 'status-passed',
      pending: 'status-pending-exec',
      bug: 'status-failed',
      improvement: 'status-warning',
      blocked: 'status-blocked',
      retest: 'status-retest'
    };
    return map[status] || 'status-unknown';
  };

  const getStatusBadgeStyle = (status) => {
    const def = Array.isArray(statusDefinitions)
      ? statusDefinitions.find(d => d.domain === 'test_cycle' && d.key === status)
      : null;
    const color = def?.color;
    // Sem cor customizada pelo admin: deixa a classe CSS cuidar (fundo claro + texto colorido, já com bom contraste).
    if (!color) return undefined;
    // Cor customizada definida pelo admin: tratada como cor sólida, então o texto precisa ser branco pra continuar legível.
    return { backgroundColor: color, borderColor: color, color: '#ffffff' };
  };

  const PRIORITY_BADGE = {
    highest: { label: 'Alta', color: '#D6394A', background: '#FCEAEC', border: '#F5CBD1' },
    critical: { label: 'Alta', color: '#D6394A', background: '#FCEAEC', border: '#F5CBD1' },
    high: { label: 'Alta', color: '#D6394A', background: '#FCEAEC', border: '#F5CBD1' },
    medium: { label: 'Média', color: '#B4740A', background: '#FBF0DC', border: '#F0DDB0' },
    low: { label: 'Baixa', color: '#5B6472', background: '#EEF0F3', border: '#D1D6DE' },
    lowest: { label: 'Baixa', color: '#5B6472', background: '#EEF0F3', border: '#D1D6DE' }
  };
  const getPriorityBadge = (priority) => PRIORITY_BADGE[String(priority || '').toLowerCase()] || null;
  const renderPriorityPill = (priority, size = 'md') => {
    const badge = getPriorityBadge(priority);
    if (!badge) return null;
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          fontSize: size === 'sm' ? 10.5 : 11.5,
          fontWeight: 650,
          color: badge.color,
          background: badge.background,
          border: `1px solid ${badge.border}`,
          borderRadius: 100,
          padding: size === 'sm' ? '1px 8px' : '3px 10px',
          whiteSpace: 'nowrap'
        }}
      >
        {badge.label}
      </span>
    );
  };

  const getStatusLabel = (status) => {
    const opt = statusOptions.find(o => o.value === status);
    if (opt) return opt.label;
    const def = Array.isArray(statusDefinitions)
      ? statusDefinitions.find(d => d.domain === 'test_cycle' && d.key === status)
      : null;
    return def?.label || (status || '—');
  };

  // Explicação de quando usar cada status — só existe pra quem realmente
  // costuma gerar dúvida na equipe (ex.: Bloqueado x Bug).
  const getStatusHelp = (status) => STATUS_HELP[status] || null;

  const getSelectableStatusOptions = (currentStatus) => {
    if (currentStatus === 'pending') {
      return statusOptions.filter(opt => opt.value === 'pending' || opt.value === 'running');
    }
    return statusOptions;
  };

  // Permissão efetiva para editar casos do ciclo
  const canEditCycleCases = useMemo(() => {
    try {
      // QA/ADM e contas individuais (TRIAL/UNITARIO) podem editar casos durante execução
      const roleUpper = String(user?.role || '').toUpperCase();
      if (['ADM', 'ADMIN', 'QA', 'TESTER', 'TRIAL', 'UNITARIO'].includes(roleUpper)) return true;
      return !!hasPermission?.('testcycles.update', ['QA', 'ADMIN', 'ADM']);
    } catch (_) {
      return false;
    }
  }, [hasPermission, user?.role]);

  // Read-only efetivo combinando prop, status do ciclo e permissão


  // Um ciclo "concluído" ainda pode ter bugs/bloqueios pendentes de correção
  // (ver STATUS_HELP): nesse caso ele não está de fato encerrado, então não
  // trava a edição — senão não dá pra marcar "retestar" após o fix.
  const hasOpenIssues = useMemo(() => {
    const list = Array.isArray(cases) ? cases : [];
    return list.some(c => {
      const s = statusByCaseId[String(c.id)]?.status;
      return s === 'bug' || s === 'blocked';
    });
  }, [cases, statusByCaseId]);

  const effectiveReadOnly = useMemo(() => {
    const statusCompleted = String(cycleStatus || '').toLowerCase() === 'completed';
    const reopenedForIssues = statusCompleted && hasOpenIssues;
    // Bloqueia edição quando: sem permissão, prop read-only, ou parado e sem
    // pendências (planejado/pausado/abortado, ou concluído de fato/sem bugs).
    return !!(isReadOnly || !canEditCycleCases || (!isExecuting && !reopenedForIssues));
  }, [isReadOnly, canEditCycleCases, isExecuting, cycleStatus, hasOpenIssues]);

  const canManageEvidences = useMemo(() => {
    return !!(canEditCycleCases && selectedCaseId && runId);
  }, [canEditCycleCases, selectedCaseId, runId]);

  

  const cycleCounters = useMemo(() => {
    const totals = { planned: 0, running: 0, completed: 0, bug: 0, improvement: 0, blocked: 0, retest: 0, pending: 0, total: 0 };
    const list = Array.isArray(cases) ? cases : [];
    totals.total = list.length;
    list.forEach(c => {
      const s = statusByCaseId[String(c.id)]?.status || 'planned';
      if (totals[s] !== undefined) totals[s] += 1;
    });
    const falseCompleted = totals.completed + totals.blocked;
    // Só isso conta como progresso — igual à regra usada no card do kanban de
    // Execuções: pendente e em andamento não contam, o resto sim.
    const executed = totals.total - totals.planned - totals.pending - totals.running;
    // Percentual de sucesso (Completed/Passed) — mantido por compatibilidade com o texto de resumo
    const percentCompleted = totals.total ? Math.round((falseCompleted / totals.total) * 100) : 0;
    // Percentual de bugs
    const percentBug = totals.total ? Math.round((totals.bug / totals.total) * 100) : 0;
    // Segmentos da barra única: só o que conta como progresso, proporcional ao total
    const segments = CYCLE_PROGRESS_SEGMENTS
      .map(seg => ({ ...seg, count: totals[seg.key] || 0, percent: totals.total ? (totals[seg.key] || 0) / totals.total * 100 : 0 }))
      .filter(seg => seg.count > 0);
    const percentExecuted = totals.total ? Math.round((executed / totals.total) * 100) : 0;
    return { ...totals, falseCompleted, percentCompleted, percentBug, executed, segments, percentExecuted };
  }, [cases, statusByCaseId]);

  // Carregar detalhes completos do caso ao selecionar, se estiverem faltando (modo lite)
  useEffect(() => {
    if (tutorial?.isTutorialMode) return;
    if (selectedCaseId && cases.length > 0) {
      const c = cases.find(x => String(x.id) === String(selectedCaseId));
      // Se não tem steps, assume que foi carregado em modo lite e busca detalhes
      if (c && c.steps === undefined) {
        const token = localStorage.getItem('token');
        fetch(getApiUrl(`testcases/${selectedCaseId}`), {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        .then(res => res.json())
        .then(data => {
           if (data.success && data.testCase) {
             setCases(prev => prev.map(tc => String(tc.id) === String(selectedCaseId) ? { ...tc, ...data.testCase } : tc));
           }
        })
        .catch(err => console.error('Erro ao carregar detalhes do caso:', err));
      }
    }
  }, [selectedCaseId, cases, tutorial?.isTutorialMode]);

  // Carregar casos do ciclo e os status já existentes (test_results)
  const loadData = useCallback(async (cycleIdParam, runIdParam) => {
    if (!cycleIdParam || !runIdParam) return;
    setLoading(true);
    setLoadingProgress(0);
    // Declarar fora do bloco try para permitir retorno após finally
    let runStatusLocal = null;
    
    try {
      if (tutorial?.isTutorialMode) {
        const caseList = tutorial?.getSimulatedCycleCases?.(cycleIdParam) || [];
        const storedStatuses = tutorial?.getSimulatedStatusByCaseId?.(runIdParam) || {};
        const statusMap = {};
        (Array.isArray(caseList) ? caseList : []).forEach((c) => {
          const cid = c?.id;
          if (!cid) return;
          const stored = storedStatuses[String(cid)];
          statusMap[String(cid)] = {
            status: stored?.status || 'planned',
            resultId: `tut-res-${String(runIdParam)}-${String(cid)}`,
          };
        });
        setCases(Array.isArray(caseList) ? caseList : []);
        setStatusByCaseId(statusMap);
        setProjectId(caseList?.[0]?.project_id || null);
        setCycleJiraStoryKey(null);
        setCorrectionByCaseId({});
        setCycleStatus('running');
        setLoadingProgress(100);
        runStatusLocal = 'running';
        return runStatusLocal;
      }

      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Helper para progresso
      let currentProgress = 0;
      const updateProgress = (val) => {
        currentProgress += val;
        setLoadingProgress(Math.min(currentProgress, 100));
      };

      // 1) Casos vinculados ao ciclo (Paralelo) - Modo Lite para performance
      const casesPromise = fetch(getApiUrl(`test-repository/test-cycles/${cycleIdParam}/cases?lite=true`), { headers })
        .then(async res => {
          const casesData = res.ok ? (await res.json()) : [];
          const caseList = Array.isArray(casesData) ? casesData : (Array.isArray(casesData?.data) ? casesData.data : []);
          setCases(caseList);
          updateProgress(30);
          return caseList;
        });

      // 2) Histórico da execução (Paralelo)
      const historyPromise = fetch(getApiUrl(`executions/runs/${runIdParam}/history`), { headers })
        .then(async res => {
          if (res.ok) {
            const histData = await res.json().catch(() => ({}));
            const history = Array.isArray(histData?.data) ? histData.data : [];
            const corrMap = {};
            history.forEach(h => {
              const action = String(h.action_type || '');
              if (action === 'correction_available') {
                let meta = h.metadata;
                if (meta && typeof meta === 'string') {
                  try { meta = JSON.parse(meta); } catch (_) {}
                }
                const cid = meta?.test_case_id;
                if (cid) corrMap[String(cid)] = true;
              }
            });
            setCorrectionByCaseId(corrMap);
          }
          updateProgress(20);
        }).catch(() => {});

      // 3) Migrar status (Fire and forget) e depois buscar detalhes do Run (Paralelo)
      fetch(getApiUrl(`executions/runs/${runIdParam}/migrate-cycle-statuses`), {
        method: 'POST',
        headers
      }).catch(() => {});
      
      const runPromise = (async () => {
        // Buscar detalhes do run
        const runRes = await fetch(getApiUrl(`executions/runs/${runIdParam}`), { headers });
        let statusMap = {};
        let localStatus = null;
        
        if (runRes.ok) {
          const runData = await runRes.json();
          const results = runData?.data?.test_results || runData?.test_results || [];
          const storyKey = runData?.data?.jira_story_key || runData?.jira_story_key || null;
          setCycleJiraStoryKey(storyKey);

          if (!subtasksTriggeredRef.current && storyKey && cycleIdParam) {
            subtasksTriggeredRef.current = true;
            createExecutionSubtasks(cycleIdParam, storyKey);
          }

          results.forEach(r => {
            // utils/executionProgress.js — mesma regra canônica do backend e
            // de Executions.js (a versão local antiga não fazia lowercase
            // antes de comparar, então um status vindo capitalizado sumia
            // da contagem sem aviso).
            const normalized = normalizeProgressBarStatus(r.status);
            // Se o caso está running, tenta recuperar o timestamp do sessionStorage
            let caseStartedAt = r.case_started_at || null;
            if (normalized === 'running') {
              const stored = sessionStorage.getItem(`timer_${runId}_${r.test_case_id}`);
              if (stored) caseStartedAt = stored;
            }
            statusMap[String(r.test_case_id)] = {
              status: normalized,
              resultId: r.id,
              caseStartedAt,
              executedAt: r.executed_at || null
            };
          });
          const pid = runData?.data?.project_id || runData?.project_id || null;
          setProjectId(pid);
          localStatus = String(runData?.data?.status || runData?.status || '');
          setCycleStatus(localStatus);
        }
        setStatusByCaseId(statusMap);
        updateProgress(50); // Aumentei o peso já que migração não conta mais
        return localStatus;
      })();

      // Aguardar tudo (menos a migração)
      const [, , runStatusResult] = await Promise.all([casesPromise, historyPromise, runPromise]);
      runStatusLocal = runStatusResult;
      
      setLoadingProgress(100);
    } catch (e) {
      console.error('Erro ao carregar dados do ciclo:', e);
      showError('Falha ao carregar casos do ciclo');
    } finally {
      setLoading(false);
    }
    return runStatusLocal;
  }, [showError, createExecutionSubtasks, tutorial]);

  useEffect(() => {
    if (!isOpen) return;
    subtasksTriggeredRef.current = false;
  }, [isOpen, cycleId]);

  // Revalidar autoconclusão do ciclo (sem alterar status dos casos)
  const revalidateCycleCompletion = useCallback(async (runIdParam) => {
    if (!runIdParam) return false;
    try {
      const res = await fetch(getApiUrl(`executions/runs/${runIdParam}/revalidate-cycle-completion`), {
        method: 'POST',
        headers: tokenHeader()
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return false;
      if (data?.auto_completed) {
        return true;
      }
      return false;
    } catch (_) {
      // silencioso: revalidação não deve interromper fluxo do usuário
      return false;
    }
  }, []);

  useEffect(() => {
    if (isOpen && cycleId && runId) {
      (async () => {
        const status = await loadData(cycleId, runId);
        if (status === 'completed') {
          setCycleStatus('completed');
        }
        const autoCompleted = await revalidateCycleCompletion(runId);
        if (autoCompleted) {
          setCycleStatus('completed');
        }
      })();
    }
  }, [isOpen, cycleId, runId, loadData, revalidateCycleCompletion]);

  useEffect(() => {
    if (!isOpen) return;
    fetchStatusDefinitions();
    if (projectId) {
      fetchProjectStatusOverrides(projectId);
      fetchIssuesForProject(projectId);
    }
  }, [isOpen, projectId]);

  useEffect(() => {
    if (isOpen && cases.length > 0 && folderFilter !== 'all') {
      const filterExists = cases.some(c => String(c.folder_id || 'main') === folderFilter);
      if (!filterExists) {
        setFolderFilter('all');
      }
    }
  }, [isOpen, cases, folderFilter]);

  useEffect(() => {
    if (isOpen && visibleCases.length > 0) {
      if (!selectedCaseId) {
        setSelectedCaseId(String(visibleCases[0].id));
      } else {
        const exists = visibleCases.some(c => String(c.id) === String(selectedCaseId));
        if (!exists) setSelectedCaseId(String(visibleCases[0].id));
      }
    }
  }, [isOpen, visibleCases, selectedCaseId]);

  useEffect(() => {
    setEditingDuration(false);
    setStatusMenuOpen(false);
    setStatusMenuPosition(null);
  }, [selectedCaseId]);

  useEffect(() => {
    if (!statusMenuOpen) return;
    const closeMenu = () => { setStatusMenuOpen(false); setStatusMenuPosition(null); };
    const handleClickOutside = (e) => {
      if (!e.target.closest('.status-select-trigger') && !e.target.closest('.status-select-menu')) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', closeMenu, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', closeMenu, true);
    };
  }, [statusMenuOpen]);

  useEffect(() => {
    if (selectedCase) {
      setDetails({
        description: selectedCase.description || '',
        preconditions: selectedCase.preconditions || '',
        steps: selectedCase.steps || ''
      });
      // Carregar evidências do caso selecionado
      fetchEvidences(selectedCase.id);
    } else {
      setEvidences([]);
    }
  }, [selectedCase, fetchEvidences]);

  // Effect 1: detecta quando o caso selecionado entra/sai de 'running'
  useEffect(() => {
    const caseData = statusByCaseId[String(selectedCaseId)];
    if (caseData?.status === 'running' && caseData?.caseStartedAt) {
      setActiveCaseStartedAt(caseData.caseStartedAt);
    } else {
      setActiveCaseStartedAt(null);
    }
  }, [selectedCaseId, statusByCaseId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 2: roda o interval apenas quando activeCaseStartedAt muda — não é afetado por outros updates de statusByCaseId
  useEffect(() => {
    clearInterval(timerIntervalRef.current);
    if (activeCaseStartedAt) {
      const startMs = new Date(activeCaseStartedAt).getTime();
      const tick = () => {
        const elapsed = Math.floor((Date.now() - startMs) / 1000);
        setTimerDisplay(formatElapsed(Math.max(0, elapsed)));
      };
      tick();
      timerIntervalRef.current = setInterval(tick, 1000);
    } else {
      setTimerDisplay(null);
    }
    return () => clearInterval(timerIntervalRef.current);
  }, [activeCaseStartedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const getLinkedIssueStatus = (caseId) => {
    const issue = issuesByCaseId[String(caseId)];
    return String(issue?.status || '').toLowerCase();
  };

  const getBlockMessage = (caseId, currentStatus) => {
    const st = getLinkedIssueStatus(caseId);
    // Bloqueio somente quando há item em correção ou reaberto
    if (['bug','improvement'].includes(currentStatus)) {
      if (st === 'in_progress' || st === 'reopened') {
        return 'Este caso está vinculado a um bug em correção. Aguarde o desenvolvedor finalizar antes de alterar o status.';
      }
      // Para corrigido/approved, não bloquear: apenas avisar sobre reabertura
      if (st === 'fixed' || st === 'approved') {
        return 'Este bug foi corrigido anteriormente. Ao manter como BUG, o item será marcado como REABERTO para nova correção.';
      }
    }
    return null;
  };

  const isHardBlock = (caseId, currentStatus) => {
    const st = getLinkedIssueStatus(caseId);
    return ['bug','improvement'].includes(currentStatus) && (st === 'in_progress' || st === 'reopened');
  };

  

  const handleStatusChange = async (caseId, newStatus) => {
    if (effectiveReadOnly) {
      // Bloqueio de edição quando execução está concluída ou sem permissão
      showError('Somente leitura — alterações não são permitidas.');
      return;
    }
    if (!runId || !caseId || !newStatus) return;

    const current = statusByCaseId[String(caseId)]?.status || 'planned';
    if (current === 'pending' && !['pending', 'running'].includes(newStatus)) {
      showError('Caso pendente só pode ser alterado para Executando.');
      return;
    }
    if (current !== 'pending' && newStatus === 'pending') {
      showError('Não pode voltar para o status anterior.');
      return;
    }

    if (tutorial?.isTutorialMode) {
      setUpdatingCaseId(caseId);
      try {
        tutorial?.setSimulatedCaseStatus?.(runId, caseId, newStatus);
        setStatusByCaseId(prev => ({
          ...(prev || {}),
          [String(caseId)]: { status: newStatus, resultId: prev?.[String(caseId)]?.resultId }
        }));
        showSuccess('Status atualizado');
        try {
          const ev = new CustomEvent('cycleCaseStatusUpdated', { detail: { runId, executionId } });
          window.dispatchEvent(ev);
        } catch (_) {}
      } finally {
        setUpdatingCaseId(null);
      }
      return;
    }
    // Verificação preventiva no cliente para UX melhor
    const blockMsg = getBlockMessage(caseId, current);
    if (blockMsg && !['bug','improvement'].includes(newStatus)) {
      showError(blockMsg);
      return;
    }

    // Interceptar criação de Bug para abrir modal
    if ((newStatus === 'bug' || newStatus === 'improvement') && current !== newStatus) {
      setPendingIssueCaseId(caseId);
      setPendingIssueType(newStatus);
      setShowIssueModal(true);
      return;
    }

    setUpdatingCaseId(caseId);
    try {
      const res = await fetch(getApiUrl(`executions/runs/${runId}/cases/${caseId}/status`), {
        method: 'PUT',
        headers: tokenHeader(),
        body: JSON.stringify({ status: newStatus })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(data?.error || 'Erro ao atualizar status do caso');
        return;
      }
      if (data?.auto_completed) {
        // Marcar estado local como concluído sem exibir toast específico
        setCycleStatus('completed');
      }
      // Feedback discreto e consistente
      if (newStatus === 'bug') {
        const prevIssue = issuesByCaseId[String(caseId)];
        const prevSt = String(prevIssue?.status || '').toLowerCase();
        if (['fixed','approved'].includes(prevSt)) {
          showSuccess('Bug reaberto: item marcado como REABERTO para correção.');
        } else {
          showSuccess('Status atualizado');
        }
      } else {
        showSuccess('Status atualizado');
      }
      // Atualizar mapa local
      const nowIso = new Date().toISOString();
      // Persistir/remover timestamp do timer no sessionStorage
      const timerKey = `timer_${runId}_${caseId}`;
      if (newStatus === 'running') {
        sessionStorage.setItem(timerKey, nowIso);
      } else {
        sessionStorage.removeItem(timerKey);
      }
      setStatusByCaseId(prev => ({
        ...prev,
        [String(caseId)]: {
          status: newStatus,
          resultId: prev[String(caseId)]?.resultId,
          caseStartedAt: newStatus === 'running'
            ? nowIso
            : (newStatus === 'planned' || newStatus === 'pending')
              ? null
              : prev[String(caseId)]?.caseStartedAt,
          executedAt: (newStatus === 'completed' || newStatus === 'blocked' || newStatus === 'bug' || newStatus === 'improvement' || newStatus === 'retest')
            ? nowIso
            : prev[String(caseId)]?.executedAt
        }
      }));

      // Trigger Jira status update in background
      updateSubtaskStatus(caseId, runId, newStatus);

      try {
        const ev = new CustomEvent('cycleCaseStatusUpdated', { detail: { runId, executionId } });
        window.dispatchEvent(ev);
      } catch (_) {}
      // Se usuário mudou para algo diferente de Planejado, remover tag de correção
      if (newStatus !== 'planned') {
        setCorrectionByCaseId(prev => ({ ...prev, [String(caseId)]: false }));
      }
      // Atualizar mapa de issues após operações relevantes (bug -> reabre; completed -> aprova)
      if (projectId && ['bug', 'improvement', 'completed', 'planned', 'running'].includes(newStatus)) {
        fetchIssuesForProject(projectId);
      }
    } catch (e) {
      console.error('Erro ao atualizar status do caso:', e);
      showError('Falha ao atualizar status do caso');
    } finally {
      setUpdatingCaseId(null);
    }
  };

  const openDurationEditor = (seconds) => {
    const total = Math.max(0, Math.floor(seconds || 0));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    setDurationInputs({ h: String(h).padStart(2,'0'), m: String(m).padStart(2,'0'), s: String(s).padStart(2,'0') });
    setEditingDuration(true);
  };

  const handleSaveDuration = async () => {
    if (!runId || !selectedCaseId) return;
    const h = Math.max(0, parseInt(durationInputs.h, 10) || 0);
    const m = Math.max(0, parseInt(durationInputs.m, 10) || 0);
    const s = Math.max(0, parseInt(durationInputs.s, 10) || 0);
    const totalSeconds = h * 3600 + m * 60 + s;

    setSavingDuration(true);
    try {
      const res = await fetch(getApiUrl(`executions/runs/${runId}/cases/${selectedCaseId}/duration`), {
        method: 'PUT',
        headers: tokenHeader(),
        body: JSON.stringify({ seconds: totalSeconds })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(data?.error || 'Erro ao editar tempo de execução');
        return;
      }
      const newStartedAt = data?.data?.case_started_at || null;
      setStatusByCaseId(prev => ({
        ...prev,
        [String(selectedCaseId)]: {
          ...prev[String(selectedCaseId)],
          caseStartedAt: newStartedAt
        }
      }));
      const current = statusByCaseId[String(selectedCaseId)]?.status;
      if (current === 'running' && newStartedAt) {
        sessionStorage.setItem(`timer_${runId}_${selectedCaseId}`, newStartedAt);
      }
      showSuccess('Tempo de execução atualizado');
      setEditingDuration(false);
    } catch (e) {
      console.error('Erro ao editar tempo de execução:', e);
      showError('Falha ao editar tempo de execução');
    } finally {
      setSavingDuration(false);
    }
  };

  const handleIssueCreated = async (bugData) => {
    setShowIssueModal(false);
    if (!pendingIssueCaseId) return;

    const caseId = pendingIssueCaseId;
    const issueType = String(pendingIssueType || bugData?.issueType || 'bug').toLowerCase() === 'improvement'
      ? 'improvement'
      : 'bug';
    const targetStatus = issueType === 'improvement' ? 'improvement' : 'bug';
    setUpdatingCaseId(caseId);

    const normalizeJiraKey = (value) => {
      const raw = String(value || '').trim();
      if (!raw) return '';
      return raw.replace(/.*\/browse\//i, '').replace(/\?.*$/, '').replace(/\s+/g, '').toUpperCase();
    };

    const normalizedStoryKey = normalizeJiraKey(bugData?.storyKey);
    const makeLocalComment = () => {
      const parts = [];
      const t = String(bugData?.summary || '').trim();
      const d = String(bugData?.description || '').trim();
      if (t) parts.push(`Titulo: ${t}`);
      if (d) parts.push(`Descricao: ${d}`);
      return parts.join('\n');
    };

    const uploadEvidence = async ({ jiraIssueKey } = {}) => {
      const file = bugData?.evidenceFile;
      if (!file || !runId) return;
      const form = new FormData();
      form.append('file', file);
      form.append('test_case_id', String(caseId));
      form.append('execution_id', String(runId));
      if (jiraIssueKey) form.append('jira_issue_key', String(jiraIssueKey));
      const token = localStorage.getItem('token') || '';
      const res = await fetch(getApiUrl('evidences/upload'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: form
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || 'Falha ao enviar evidencia');
      }
    };

    try {
      let jiraKey = null;
      let jiraUrl = null;

      if (issueType === 'bug' && normalizedStoryKey) {
        const resCreate = await fetch(getApiUrl('integrations/jira-basic/create-bug'), {
          method: 'POST',
          headers: tokenHeader(),
          body: JSON.stringify({
            summary: bugData.summary,
            description: bugData.description,
            storyKey: normalizedStoryKey,
            relation: bugData.relation,
            priority: bugData.priority,
            caseId,
            cycleId
          })
        });
        const dataCreate = await resCreate.json().catch(() => ({}));
        if (resCreate.ok && dataCreate?.success) {
          jiraKey = dataCreate.key || null;
          jiraUrl = dataCreate.url || null;
        } else if (dataCreate?.reason !== 'missing_story_key') {
          showError(dataCreate?.error || dataCreate?.message || 'Falha ao criar bug no Jira');
          return;
        }
      }

      const comment = jiraKey ? `Bug Jira: ${jiraKey}` : makeLocalComment();
      const resStatus = await fetch(getApiUrl(`executions/runs/${runId}/cases/${caseId}/status`), {
        method: 'PUT',
        headers: tokenHeader(),
        body: JSON.stringify({ status: targetStatus, comment, priority: bugData.priority })
      });
      const dataStatus = await resStatus.json().catch(() => ({}));
      if (!resStatus.ok) {
        showError(dataStatus?.error || (issueType === 'improvement' ? 'Falha ao criar melhoria' : 'Falha ao criar bug'));
        return;
      }

      try {
        await uploadEvidence({ jiraIssueKey: jiraKey });
      } catch (_) {
        showError(issueType === 'improvement' ? 'Melhoria criada, mas falha ao enviar evidencia' : 'Bug criado, mas falha ao enviar evidencia');
      }

      if (jiraKey && jiraUrl && issueType === 'bug') {
        showSuccess(
          <span>
            Bug <strong>{jiraKey}</strong> criado com sucesso!{' '}
            <a href={jiraUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'white', textDecoration: 'underline' }}>
              Abrir no Jira
            </a>
          </span>
        );
      } else {
        showSuccess(issueType === 'improvement' ? 'Melhoria criada em Bugs/Melhorias' : 'Bug criado em Bugs/Melhorias');
      }

      setStatusByCaseId(prev => ({
        ...prev,
        [String(caseId)]: { status: targetStatus, resultId: prev[String(caseId)]?.resultId }
      }));
      if (projectId) fetchIssuesForProject(projectId);
      navigate('/issues');
    } catch (e) {
      console.error('Erro ao processar criacao do item:', e);
      showError(issueType === 'improvement' ? 'Falha ao criar melhoria' : 'Falha ao criar bug');
    } finally {
      setUpdatingCaseId(null);
      setPendingIssueCaseId(null);
      setPendingIssueType('bug');
    }
  };
  // Relatório/caderno de evidências — disponível mesmo com o ciclo em andamento,
  // pra dar visibilidade do progresso diário e não só ao concluir.
  const downloadEvidenceNotebook = async () => {
    const targetRunId = executionId || runId;
    if (!targetRunId || downloadingNotebook) return;
    setDownloadingNotebook(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`reports/evidence-notebook/download?run_id=${encodeURIComponent(targetRunId)}&document_type=caderno`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData?.error || 'Erro ao gerar relatório');
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Relatorio de Execucao.docx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccess('Relatório gerado com sucesso.');
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      showError('Erro ao gerar relatório');
    } finally {
      setDownloadingNotebook(false);
    }
  };

  const handleClose = () => {
    if (tutorial?.isTutorialMode) {
      onClose && onClose(false);
      return;
    }
    // Sempre tenta revalidar ao fechar para garantir que execuções
    // vinculadas sejam concluídas mesmo se o ciclo já estiver concluído.
    if (runId) {
      revalidateCycleCompletion(runId)
        .then((autoCompleted) => {
          onClose && onClose(Boolean(autoCompleted));
        })
        .catch(() => {
          onClose && onClose(false);
        });
    } else {
      onClose && onClose(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="cycle-cases-inline" ref={inlineRootRef} style={inlineHeight ? { height: inlineHeight } : undefined}>
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>Casos vinculados ao ciclo</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {canExportReports && (executionId || runId) && (
              <button
                className="btn-secondary btn-sm"
                onClick={downloadEvidenceNotebook}
                disabled={downloadingNotebook}
                title="Gera um DOCX com o andamento até agora, mesmo sem o ciclo estar concluído"
              >
                📄 {downloadingNotebook ? 'Gerando...' : 'Baixar Relatório'}
              </button>
            )}
          </div>
        </div>
        <div className="modal-body cycle-cases-modal-body">
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
              <div style={{ width: '300px', height: '8px', background: '#EEF0F3', borderRadius: '999px', marginBottom: '10px', overflow: 'hidden' }}>
                <div style={{ width: `${loadingProgress}%`, height: '100%', background: '#2F63E0', borderRadius: '999px', transition: 'width 0.3s ease' }} />
              </div>
              <div style={{ fontSize: 13, color: '#5B6472' }}>Carregando casos... {loadingProgress}%</div>
            </div>
          ) : (
            <div className="cycle-cases-grid">
              <div className="cycle-cases-progress">
                <div>
                  <strong>Progresso do ciclo</strong>
                </div>
                <div style={{ width: 340 }}>
                  <div title={`Executados: ${cycleCounters.executed}/${cycleCounters.total}`} style={{ position:'relative', display:'flex', width:'100%', height:14, background:'#EEF0F3', borderRadius:999, overflow:'hidden' }}>
                    {cycleCounters.segments.map(seg => (
                      <div
                        key={seg.key}
                        title={`${seg.label}: ${seg.count}`}
                        style={{ width:`${seg.percent}%`, height:'100%', background:seg.color, transition:'width 0.3s ease' }}
                      />
                    ))}
                    <span style={{ position:'absolute', top:0, left:0, right:0, bottom:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:'#ffffff', textShadow:'0 1px 1px rgba(0,0,0,0.35)', pointerEvents:'none' }}>
                      {cycleCounters.percentExecuted}%
                    </span>
                  </div>
                  <div style={{ display:'flex', gap:12, marginTop:8, fontSize:12, fontWeight:600, flexWrap:'wrap' }}>
                    {cycleCounters.segments.map(seg => (
                      <span key={seg.key} className="qa-tooltip" data-tooltip={seg.label} style={{ display:'inline-flex', alignItems:'center', gap:4, color: seg.color, cursor:'default' }}>
                        <StatusIcon type={seg.icon} color={seg.color} size={13} /> {seg.count}
                      </span>
                    ))}
                    <span className="qa-tooltip" data-tooltip="Planejado" style={{ display:'inline-flex', alignItems:'center', gap:4, color:'#8D96A5', cursor:'default' }}>
                      <StatusIcon type="circle" color="#8D96A5" size={13} /> {cycleCounters.planned}
                    </span>
                    <span className="qa-tooltip" data-tooltip="Pendente" style={{ display:'inline-flex', alignItems:'center', gap:4, color:'#8D96A5', cursor:'default' }}>
                      <StatusIcon type="circle" color="#8D96A5" size={13} /> {cycleCounters.pending}
                    </span>
                    <span className="qa-tooltip" data-tooltip="Em andamento" style={{ display:'inline-flex', alignItems:'center', gap:4, color:'#2C80C7', cursor:'default' }}>
                      <StatusIcon type="play" color="#2C80C7" size={13} /> {cycleCounters.running}
                    </span>
                  </div>
                </div>
              </div>
              <div className="cycle-cases-list-pane" data-tour="modal-exec-casos-lista">
                <div className="cycle-cases-folder-filter">
                  <label htmlFor="cycle-cases-folder-filter">Pasta</label>
                  <select
                    id="cycle-cases-folder-filter"
                    value={folderFilter}
                    onChange={(e) => setFolderFilter(e.target.value)}
                  >
                    <option value="all">Todas as pastas ({cases.length})</option>
                    {folderFilterOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label} ({option.count})
                      </option>
                    ))}
                  </select>
                </div>
                <ul className="list" style={{ listStyle:'none', padding:0, margin:0 }}>
                  {visibleCases.map(c => {
                    const key = String(c.id);
                    const current = statusByCaseId[key]?.status || 'planned';
                    const blockMsg = getBlockMessage(c.id, current);
                    const active = String(selectedCaseId) === String(c.id);
                    return (
                      <li
                        key={c.id}
                        className={`cycle-case-list-item${active ? ' active' : ''}`}
                        data-status-accent={getStatusBadgeClass(current).replace('status-', '').replace('-exec', '')}
                        style={getStatusBadgeStyle(current) ? { borderLeftColor: getStatusBadgeStyle(current).backgroundColor } : undefined}
                        onClick={() => setSelectedCaseId(String(c.id))}
                      >
                        <div className="cycle-case-list-header">
                          <span className="cycle-case-list-title">{c.title || `Caso #${c.id}`}</span>
                          <span className="cycle-case-list-status">
                            <span
                              className={`status-badge ${getStatusBadgeClass(current)}${getStatusHelp(current) ? ' qa-tooltip qa-tooltip-wide qa-tooltip-align-right' : ''}`}
                              data-tooltip={getStatusHelp(current) || undefined}
                              style={{ ...getStatusBadgeStyle(current), fontSize:11 }}
                            >
                              {getStatusLabel(current)}
                            </span>
                          </span>
                        </div>
                        {correctionByCaseId[key] && current === 'planned' && (
                          <span style={{ marginTop:6, display:'inline-block', fontSize:11, fontWeight:600, borderRadius:'9999px', padding:'2px 9px', border:'1px solid #F0DDB0', color:'#B4740A', background:'#FBF0DC' }}>Correção recebida</span>
                        )}
                        {c.description && (
                          <div className="muted" style={{ fontSize:12, marginTop:6, lineHeight:1.45 }}>{c.description}</div>
                        )}
                        {isHardBlock(c.id, current) && (
                          <div style={{ marginTop:6, display:'inline-block', fontSize:11, fontWeight:600, borderRadius:'9999px', padding:'2px 9px', border:'1px solid #F5CBD1', color:'#D6394A', background:'#FCEAEC' }}>Edição bloqueada</div>
                        )}
                        {!isHardBlock(c.id, current) && blockMsg && (
                          <div style={{ marginTop:6, display:'inline-block', fontSize:11, fontWeight:600, borderRadius:'9999px', padding:'2px 9px', border:'1px solid #F0DDB0', color:'#B4740A', background:'#FBF0DC' }}>Aviso</div>
                        )}
                      </li>
                    );
                  })}
                  {cases.length === 0 && (
                    <li className="muted">Nenhum caso vinculado ao ciclo</li>
                  )}
                  {cases.length > 0 && visibleCases.length === 0 && (
                    <li className="muted">Nenhum caso nesta pasta</li>
                  )}
                </ul>
              </div>
              <div className="cycle-cases-detail-pane">
                {selectedCase ? (
                  <div style={{ display:'flex', flexDirection:'column', gap:18 }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', rowGap:10 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ fontSize:17, fontWeight:650, color:'#1B2130' }}>{selectedCase.title}</div>
                        {selectedCase.priority && (
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <span style={{ fontSize:10.5, fontWeight:650, letterSpacing:0.4, textTransform:'uppercase', color:'#8D96A5' }}>Prioridade</span>
                            {renderPriorityPill(selectedCase.priority)}
                          </div>
                        )}
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        {(() => {
                          const current = statusByCaseId[String(selectedCaseId)]?.status || 'planned';
                          const caseData = statusByCaseId[String(selectedCaseId)];
                          const completedDuration = current === 'completed' && caseData?.caseStartedAt && caseData?.executedAt
                            ? formatElapsed(Math.max(0, Math.floor((new Date(caseData.executedAt).getTime() - new Date(caseData.caseStartedAt).getTime()) / 1000)))
                            : null;
                          const runningSeconds = current === 'running' && caseData?.caseStartedAt
                            ? Math.max(0, Math.floor((Date.now() - new Date(caseData.caseStartedAt).getTime()) / 1000))
                            : 0;
                          const completedSeconds = completedDuration && caseData?.caseStartedAt && caseData?.executedAt
                            ? Math.max(0, Math.floor((new Date(caseData.executedAt).getTime() - new Date(caseData.caseStartedAt).getTime()) / 1000))
                            : 0;
                          const canEditDuration = !effectiveReadOnly && (current === 'running' || !!completedDuration);

                          if (editingDuration && canEditDuration) {
                            return (
                              <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', justifyContent:'flex-end' }}>
                                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                  {[{ unit:'h', suffix:'h' }, { unit:'m', suffix:'m' }, { unit:'s', suffix:'seg' }].map(({ unit, suffix }) => (
                                    <div key={unit} style={{ display:'flex', alignItems:'center', gap:4, background:'#ffffff', border:'1.5px solid #D1D6DE', borderRadius:8, padding:'6px 10px' }}>
                                      <input
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={2}
                                        value={durationInputs[unit]}
                                        onFocus={(e) => e.target.select()}
                                        onChange={(e) => {
                                          const digits = e.target.value.replace(/\D/g, '').slice(-2);
                                          setDurationInputs(prev => ({ ...prev, [unit]: digits }));
                                        }}
                                        onBlur={(e) => {
                                          const max = unit === 'h' ? 99 : 59;
                                          const n = Math.min(max, Math.max(0, parseInt(e.target.value, 10) || 0));
                                          setDurationInputs(prev => ({ ...prev, [unit]: String(n).padStart(2,'0') }));
                                        }}
                                        style={{ width:36, height:24, boxSizing:'content-box', padding:0, margin:0, background:'transparent', border:'none', outline:'none', color:'#1B2130', fontFamily:'monospace', fontSize:15, fontWeight:700, textAlign:'center', lineHeight:'24px' }}
                                      />
                                      <span style={{ color:'#8D96A5', fontSize:12, fontWeight:650 }}>{suffix}</span>
                                    </div>
                                  ))}
                                </div>
                                <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                                  <button
                                    className="btn-icon"
                                    disabled={savingDuration}
                                    onClick={handleSaveDuration}
                                    title="Salvar tempo"
                                    style={{ color:'#1C9A6C', fontSize:18 }}
                                  >✓</button>
                                  <button
                                    className="btn-icon"
                                    disabled={savingDuration}
                                    onClick={() => setEditingDuration(false)}
                                    title="Cancelar"
                                    style={{ color:'#8D96A5', fontSize:18 }}
                                  >×</button>
                                </div>
                              </div>
                            );
                          }

                          return (
                            <>
                              {current === 'running' && timerDisplay && (
                                <span style={{ fontSize:12.5, fontWeight:650, color:'#2C80C7', background:'#E9F3FC', border:'1px solid #CFE6F7', borderRadius:100, padding:'4px 11px', letterSpacing:0.5, display:'inline-flex', alignItems:'center', gap:6 }}>
                                  ⏱ {timerDisplay}
                                  {canEditDuration && (
                                    <button className="btn-icon qa-tooltip" data-tooltip="Editar tempo" onClick={() => openDurationEditor(runningSeconds)} style={{ padding:0, color:'#2C80C7', fontSize:11 }}>✎</button>
                                  )}
                                </span>
                              )}
                              {completedDuration && (
                                <span style={{ fontSize:12, fontWeight:600, color:'#1C9A6C', background:'#E6F6EF', border:'1px solid #C7EBDB', borderRadius:100, padding:'4px 11px', display:'inline-flex', alignItems:'center', gap:6 }}>
                                  ⏱ {completedDuration}
                                  {canEditDuration && (
                                    <button className="btn-icon qa-tooltip" data-tooltip="Editar tempo" onClick={() => openDurationEditor(completedSeconds)} style={{ padding:0, color:'#1C9A6C', fontSize:11 }}>✎</button>
                                  )}
                                </span>
                              )}
                              {(() => {
                                const statusDisabled = effectiveReadOnly || updatingCaseId === selectedCase.id || isHardBlock(selectedCase.id, current);
                                return (
                                  <span style={{ position: 'relative', display: 'inline-block' }}>
                                    <button
                                      type="button"
                                      ref={statusMenuTriggerRef}
                                      data-tour="modal-exec-casos-status"
                                      className={`status-badge status-select-trigger ${getStatusBadgeClass(current)}`}
                                      disabled={statusDisabled}
                                      onClick={(e) => {
                                        if (statusMenuOpen) {
                                          setStatusMenuOpen(false);
                                          setStatusMenuPosition(null);
                                          return;
                                        }
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setStatusMenuPosition({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
                                        setStatusMenuOpen(true);
                                      }}
                                      style={{ ...getStatusBadgeStyle(current), padding:'6px 12px', fontFamily:'inherit' }}
                                    >
                                      {getStatusLabel(current)}
                                      <span className="status-select-caret">▾</span>
                                    </button>
                                    {statusMenuOpen && statusMenuPosition && createPortal(
                                      <div className="status-select-menu" style={{ position: 'fixed', top: statusMenuPosition.top, right: statusMenuPosition.right }}>
                                        {getSelectableStatusOptions(current).map(opt => (
                                          <button
                                            key={opt.value}
                                            type="button"
                                            className={`status-select-option${opt.value === current ? ' active' : ''}${getStatusHelp(opt.value) ? ' qa-tooltip qa-tooltip-wide' : ''}`}
                                            data-tooltip={getStatusHelp(opt.value) || undefined}
                                            onClick={() => {
                                              setStatusMenuOpen(false);
                                              setStatusMenuPosition(null);
                                              handleStatusChange(selectedCase.id, opt.value);
                                            }}
                                          >
                                            <span className="status-select-check">{opt.value === current ? '✓' : ''}</span>
                                            {opt.label}
                                          </button>
                                        ))}
                                      </div>,
                                      document.body
                                    )}
                                  </span>
                                );
                              })()}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, alignItems:'stretch' }}>
                      <div style={{ background:'#FAFBFC', border:'1px solid #E4E7EC', borderRadius:10, padding:14 }}>
                        <div style={{ fontSize:11, fontWeight:650, letterSpacing:0.4, textTransform:'uppercase', color:'#8D96A5', marginBottom:10 }}>Gherkin</div>
                        <textarea
                          value={details.steps}
                          onChange={(e) => setDetails(d => ({ ...d, steps: e.target.value }))}
                          disabled={effectiveReadOnly}
                          style={{ width:'100%', minHeight:220, fontFamily:'inherit', fontSize:13, border:'1px solid #D1D6DE', borderRadius:8, padding:10, color:'#1B2130' }}
                        />
                      </div>
                      <div style={{ display:'flex', flexDirection:'column' }}>
                        <div style={{ background:'#FAFBFC', border:'1px solid #E4E7EC', borderRadius:10, padding:14, flex:1, display:'flex', flexDirection:'column' }}>
                          <div style={{ fontSize:11, fontWeight:650, letterSpacing:0.4, textTransform:'uppercase', color:'#8D96A5', marginBottom:10 }}>Pré-condição</div>
                          <textarea
                            value={details.preconditions}
                            onChange={(e) => setDetails(d => ({ ...d, preconditions: e.target.value }))}
                            disabled={effectiveReadOnly}
                            style={{ width:'100%', flex:1, minHeight:90, resize:'none', fontFamily:'inherit', fontSize:13, border:'1px solid #D1D6DE', borderRadius:8, padding:10, color:'#1B2130' }}
                          />
                        </div>
                      </div>
                    </div>
                    <div style={{ background: '#FAFBFC', border: '1px solid #E4E7EC', borderRadius: 10, padding: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <div style={{ fontSize:11, fontWeight:650, letterSpacing:0.4, textTransform:'uppercase', color:'#8D96A5' }}>Evidências</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleUploadEvidence}
                            style={{ display: 'none' }}
                            accept="image/*,video/*"
                          />
                          <button
                            style={{ padding: '5px 12px', fontSize: 12, fontWeight:600, borderRadius:100, border:'1px solid #D6E3FC', background:'#EAF0FE', color:'#2F63E0', cursor:'pointer' }}
                            data-tour="modal-exec-casos-evidencia"
                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                            disabled={uploadingEvidence || !canManageEvidences}
                          >
                            {uploadingEvidence ? 'Enviando...' : '+ Adicionar'}
                          </button>
                        </div>
                      </div>

                      {loadingEvidences ? (
                        <div className="muted" style={{ fontSize: 12 }}>Carregando...</div>
                      ) : evidences.length > 0 ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8 }}>
                          {evidences.map(ev => (
                            <div key={ev.id} style={{ position: 'relative', border: '1px solid #E4E7EC', borderRadius: 8, padding: 4, background: '#fff' }}>
                              <div
                                style={{
                                  height: 60,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: '#F5F6F8',
                                  borderRadius: 6,
                                  fontSize: 10,
                                  color: '#8D96A5',
                                  cursor: 'pointer',
                                  marginBottom: 4,
                                  overflow: 'hidden'
                                }}
                                onClick={() => handleViewEvidence(ev)}
                                title={ev.file_name}
                              >
                                {ev.mime_type?.startsWith('image/') && ev.url ? (
                                  <img
                                    src={ev.url}
                                    alt={ev.file_name}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                ) : (
                                  ev.mime_type?.startsWith('image/') ? '📷 Imagem' : '📹 Vídeo'
                                )}
                              </div>
                              <div style={{ fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color:'#5B6472' }} title={ev.file_name}>
                                {ev.file_name}
                              </div>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeleteEvidence(ev); }}
                                style={{
                                  position: 'absolute',
                                  top: -5,
                                  right: -5,
                                  background: '#D6394A',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '50%',
                                  width: 18,
                                  height: 18,
                                  fontSize: 10,
                                  cursor: 'pointer',
                                  display: canManageEvidences ? 'flex' : 'none',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="muted" style={{ fontSize: 12 }}>Nenhuma evidência anexada</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="muted">Selecione um caso na lista</div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-primary" data-tour="salvar-caso" onClick={handleSaveDetails} disabled={savingDetails || effectiveReadOnly || !selectedCase}>
            {savingDetails ? 'Salvando...' : 'Salvar'}
          </button>
          <button className="btn-secondary" onClick={handleClose}>← Voltar</button>
        </div>
      </div>
      {showIssueModal && (
        <div onClick={(e) => e.stopPropagation()}>
          <CreateBugModal
              isOpen={showIssueModal}
              onClose={() => { setShowIssueModal(false); setPendingIssueCaseId(null); setPendingIssueType('bug'); }}
              onCreate={handleIssueCreated}
              storyKey={cycleJiraStoryKey}
              caseId={pendingIssueCaseId}
              cycleId={cycleId}
              issueType={pendingIssueType}
          />
        </div>
      )}
      {previewEvidence && (
        <div 
            className="modal-overlay" 
            style={{ zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.85)' }}
            onClick={() => setPreviewEvidence(null)}
        >
            <div 
                style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }} 
                onClick={(e) => e.stopPropagation()}
            >
                <button 
                    onClick={() => setPreviewEvidence(null)}
                    style={{
                        position: 'absolute',
                        top: -40,
                        right: 0,
                        background: 'transparent',
                        border: 'none',
                        color: 'white',
                        fontSize: 32,
                        cursor: 'pointer',
                        lineHeight: 1
                    }}
                >
                    &times;
                </button>
                
                {previewEvidence.mime_type?.startsWith('video/') ? (
                    <video 
                        src={previewEvidence.url} 
                        controls 
                        autoPlay 
                        style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: 4, display: 'block' }}
                    />
                ) : (
                    <img 
                        src={previewEvidence.url} 
                        alt={previewEvidence.file_name} 
                        style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: 4, display: 'block' }}
                    />
                )}
            </div>
        </div>
      )}
      {evidenceToDelete && (
        <div className="modal-overlay" onClick={() => setEvidenceToDelete(null)} style={{ zIndex: 10000 }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Excluir evidência</h2>
              <button className="modal-close" onClick={() => setEvidenceToDelete(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p>
                Tem certeza que deseja excluir a evidência <strong>{evidenceToDelete.file_name}</strong>?
              </p>
              <div style={{
                marginTop: 12,
                padding: '12px 14px',
                border: '1px solid #fca5a5',
                background: '#fef2f2',
                color: '#7f1d1d',
                borderRadius: 6
              }}>
                <strong>Atenção:</strong> Esta ação é permanente e <strong>não pode ser desfeita</strong>.
              </div>
            </div>
            <div className="modal-actions" style={{ padding: 20 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setEvidenceToDelete(null)}>Cancelar</button>
              <button type="button" className="btn btn-danger" onClick={confirmDeleteEvidence}>🗑️ Excluir</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CycleCasesModal;
